import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateScopedSecretRequest, UpdateScopedSecretRequest } from '@reflex/shared';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { Skeleton } from '@reflex/ui/components/ui/skeleton';
import { ScopedSecretsManager } from '@/components/secrets/ScopedSecretsManager';
import { NestedDetailHeader } from '@/components/shared/NestedDetailHeader';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { usePermission } from '@reflex/ui/client/permissions';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { api } from '@/lib/api';

/**
 * Team secrets page (`/{orgSlug}/org/teams/:teamId/secrets`).
 *
 * Surfaces the same ID-first CRUD model as the user secrets page,
 * scoped to a single team. Authorization mirrors the route layer
 * (`teams:manage` within this team); for non-managers the page renders
 * read-only.
 */
export function TeamSecretsPage({ embedded = false }: { embedded?: boolean }) {
  const { teamId } = useParams<{ teamId: string }>();
  const id = teamId ?? '';
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const routes = useOrgRoutes();
  // Checked within this team, matching the route gate: a member whose team
  // role carries `teams:manage` manages their own team's secrets.
  const canManage = usePermission('teams:manage', { team: id });
  const queryClient = useQueryClient();

  const wrap = (content: ReactNode) =>
    embedded ? content : <PageLayout maxWidth="4xl">{content}</PageLayout>;

  const teamQuery = useQuery({
    queryKey: ['teams', id],
    queryFn: () => api.getTeam(id),
    enabled: !!id,
  });

  const secretsQuery = useQuery({
    queryKey: ['teams', id, 'secrets'],
    queryFn: () => api.listTeamSecrets(orgId, id),
    enabled: !!id && !!orgId && canManage,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateScopedSecretRequest) => api.createTeamSecret(orgId, id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', id, 'secrets'] });
      toast.success('Secret saved');
    },
    onError: (err: Error) => toast.error('Failed to save secret', { description: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ secretId, data }: { secretId: string; data: UpdateScopedSecretRequest }) =>
      api.updateTeamSecret(orgId, id, secretId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', id, 'secrets'] });
      toast.success('Secret updated');
    },
    onError: (err: Error) => toast.error('Failed to update secret', { description: err.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: (secretId: string) => api.deleteTeamSecret(orgId, id, secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', id, 'secrets'] });
      toast.success('Secret deleted');
    },
    onError: (err: Error) => toast.error('Failed to delete secret', { description: err.message }),
  });

  const teamSecretsHeader = {
    iconVariant: 'box' as const,
    icon: <KeyRound className="h-5 w-5 text-muted-foreground" />,
    title: 'Team secrets',
    docs: 'administration/security#secrets-tiers',
  };

  if (!id) {
    return wrap(
      !embedded ? <PageHeader {...teamSecretsHeader} description="Missing team id." /> : null,
    );
  }

  if (teamQuery.isLoading) {
    return wrap(
      <>
        {!embedded && <PageHeader {...teamSecretsHeader} description="Loading team…" />}
        <Skeleton className="h-32 w-full" />
      </>,
    );
  }

  const team = teamQuery.data;
  if (teamQuery.isError || !team) {
    return wrap(
      <>
        {!embedded && (
          <NestedDetailHeader
            back={{ to: routes.teams, label: 'Teams', ariaLabel: 'Back to teams' }}
            {...teamSecretsHeader}
            description="This team could not be loaded."
          />
        )}
      </>,
    );
  }

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return wrap(
    <>
      {!embedded && (
        <NestedDetailHeader
          back={{ to: routes.teams, label: 'Teams', ariaLabel: 'Back to teams' }}
          {...teamSecretsHeader}
          title={`${team.name} secrets`}
          description="Secrets owned by this team. Injected into agent devboxes for any team member at launch."
        />
      )}

      <ScopedSecretsManager
        title="Team Secrets"
        description={
          canManage
            ? 'Add team-wide secrets here. Members launching agents will see these as environment variables in their devbox.'
            : 'You do not have permission to manage team secrets.'
        }
        secrets={secretsQuery.data ?? []}
        isLoading={secretsQuery.isLoading}
        isPending={isPending}
        readOnly={!canManage}
        emptyState={
          canManage
            ? 'No team secrets yet. Add one to share credentials across this team.'
            : 'No team secrets configured.'
        }
        onCreate={(name, value) => createMutation.mutate({ name, value })}
        onUpdate={(secretId, value) => updateMutation.mutate({ secretId, data: { value } })}
        onDelete={(secretId) => deleteMutation.mutate(secretId)}
      />
    </>,
  );
}
